/**
 * The form controls the sign-in screens share.
 *
 * Extracted from `Account.tsx` on 2026-09-14 because `SignInOptions` had grown
 * its own copies with hand-written inline styles — and those styles named
 * tokens that **do not exist**: `--line`, `--surface`, `--text`. An undefined
 * CSS variable is not an error, it is simply ignored, so those inputs rendered
 * with no background, no border and inherited colour on a screen where every
 * other field had all three. The password form and the phone form sat one above
 * the other looking like two different products.
 *
 * The tokens are `--card`, `--card-brd`, `--txt`, `--muted`, `--faint`,
 * `--accent`, `--bad`, `--tap` (see `ui/tokens.css`). There is no sensible way
 * to catch a typo in a `var()` name from a test, which is exactly why these
 * live in one place now rather than being retyped per screen.
 */
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cx } from '@/ui';
import './authControls.css';

export function Field({
  label, type, value, onChange, hint, className, ...rest
}: {
  label: string; type: string; value: string; onChange: (v: string) => void; hint?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'type' | 'onChange'>) {
  return (
    <label className="auth-controls-label">
      <span className="auth-controls-caption">{label}</span>
      <input
        {...rest}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cx('auth-controls-input', className)}
      />
      {hint && <span className="auth-controls-hint">{hint}</span>}
    </label>
  );
}

export function Link({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="auth-controls-link">
      {children}
    </button>
  );
}

/**
 * An error a person can act on.
 *
 * `role="alert"` so a screen reader announces it — a sign-in failure that is
 * only visible is a sign-in failure some people cannot perceive at all.
 */
export function Alert({ children }: { children: ReactNode }) {
  return <div role="alert" className="auth-controls-alert">{children}</div>;
}
